import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { FileCheck, UserCheck, Send } from 'lucide-react';
import { Button } from './ui/button';

export const ServicesSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    {
      icon: UserCheck,
      title: 'Free Visa Consultation',
      description: 'Get expert advice on the best visa options for your specific situation. Our experienced consultants will assess your case and provide personalized recommendations.',
      price: 'FREE',
      features: [
        'Initial case assessment',
        'Visa type recommendation',
        'Requirements overview',
        'Timeline estimation'
      ],
      color: 'from-accent to-accent/80'
    },
    {
      icon: FileCheck,
      title: 'Document Collection & Preparation',
      description: 'Let us handle all the paperwork. We ensure your documents are properly collected, reviewed, and prepared for a successful visa application.',
      price: '$100',
      features: [
        'Document checklist provision',
        'Document review & verification',
        'Application form completion',
        'Submission preparation'
      ],
      color: 'from-primary to-secondary'
    }
  ];

  return (
    <section id="services" className="py-20 sm:py-24 lg:py-32 bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Our Services
          </h2>
          <p className="text-lg text-muted-foreground">
            We offer comprehensive visa and immigration services tailored to your needs
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          {services.map((service, index) => (
            <Card
              key={index}
              className="border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-xl group"
            >
              <CardHeader>
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} mb-4 group-hover:scale-110 transition-transform`}>
                  <service.icon className="h-7 w-7 text-white" />
                </div>
                <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  {service.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col">
                <div className="mb-6">
                  <div className="flex items-baseline mb-4">
                    <span className={`text-4xl font-bold bg-gradient-to-r ${service.color} bg-clip-text text-transparent`}>
                      {service.price}
                    </span>
                  </div>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg
                          className="h-5 w-5 text-accent mr-3 mt-0.5 flex-shrink-0"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Button
                  onClick={scrollToContact}
                  className="w-full mt-auto bg-gradient-primary text-primary-foreground hover:opacity-90"
                >
                  Get Started
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="max-w-3xl mx-auto mt-12 p-6 bg-muted/50 rounded-2xl border border-border">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 bg-accent/10 p-3 rounded-lg">
              <Send className="h-6 w-6 text-accent" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Complete Application Support</h3>
              <p className="text-muted-foreground leading-relaxed">
                From initial consultation to final submission, we guide you through every step of the visa application process. 
                Our expertise ensures accuracy and increases your chances of approval.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
